import React from 'react';
import { FadeIn } from './FadeIn';
interface Role {
  title: string;
  period?: string;
  summary?: string;
  bullets?: string[];
}
interface Company {
  company: string;
  location: string;
  roles: Role[];
}
const experience: Company[] = [{
  company: 'Citigroup',
  location: 'New York, NY',
  roles: [{
    title: 'SVP, Program Management Senior Lead',
    period: '05/2025 – Present',
    summary: 'Driving enterprise risk and technology transformation initiatives, overseeing reporting and analytics, executive and board-level communications, regulatory governance, and execution oversight across global risk and technology programs.',
    bullets: ['Lead development of enterprise Risk Transformation reporting and executive dashboards for senior management and board oversight.', 'Prepare governance and operating committee materials to facilitate decision-making for risk and control transformation programs.', 'Contribute to regulatory reporting and quarterly updates on enterprise risk transformation initiatives to keep stakeholders informed.']
  }, {
    title: 'Senior Risk Policy Officer',
    period: '01/2024 – 05/2025',
    summary: 'Led policy initiatives within the Enterprise Risk Management Framework, establishing policy governance, alignment across Lines of Defense, and compliance with enterprise risk management standards.',
    bullets: ['Drove oversight processes and governance alignment across enterprise risk management functions.', 'Developed executive communications, analytics, and reporting to support stakeholder engagement and board-level updates.', 'Managed regulatory and audit engagements, ensuring compliance with enterprise risk standards and regulatory requirements.']
  }, {
    title: 'Senior Project Manager Specialist',
    period: '01/2023 – 10/2023',
    summary: 'Partnered with Liquidity Risk Management to lead end-to-end delivery of regulatory, audit, and risk governance initiatives.',
    bullets: ['Led the Global Liquidity Limits Framework Program, supporting enterprise liquidity risk governance and regulatory compliance initiatives.', 'Represented Global Liquidity Risk Management in investment planning, budgeting processes, and strategic portfolio analysis.', 'Partnered cross-functionally with technology, audit, and business stakeholders to implement system enhancements, regulatory remediation, and risk compliance initiatives.']
  }]
}, {
  company: 'Credit Suisse',
  location: 'New York, NY',
  roles: [{
    title: 'Vice President, Project Manager',
    period: '08/2015 – 01/2023',
    summary: 'Owned global delivery of financial regulatory technology programs across Repo, Collateral, and Margin Services — including Brexit, MiFID, IOSCO, FATCA, FINRA 4210, FINMA, and SOX-BEAT compliance.',
    bullets: ['Led delivery of the Margin Services and Collateral application platform, managing a $10M+ project portfolio across regulatory and business initiatives.', 'Directed global Agile/SDLC software development programs, leading a matrix team of 25+ resources and overseeing the program book of work.', 'Oversaw delivery of the Margin Services & Collateral technology platform, managing a $10M+ portfolio of regulatory and business transformation projects.']
  }, {
    title: 'AVP, Business Continuity Analyst',
    period: '03/2010 – 07/2015',
    summary: 'Supported enterprise resilience, crisis management planning, and operational continuity strategies.',
    bullets: ['Conducted Business Impact Analyses (BIA) to identify critical processes, resilience strategies, and operational continuity gaps.', 'Coordinated loss-of-premises and end-to-end process testing with business and IT teams to validate recovery readiness.', 'Led initiatives to streamline impact-specific testing and sign-off processes for split operations and operational transfer scenarios.']
  }, {
    title: 'Project Manager, Business Manager',
    period: '10/2008 – 02/2010',
    summary: 'Managed a global multi-million-dollar budget supporting Fixed Income Production Services operations.',
    bullets: ['Served as liaison between PSG-FID, Fixed Income IT, and leadership, overseeing budget management, financial controls, and project coordination.', 'Managed PSG-FID budget tracking and financial controls while facilitating collaboration between Fixed Income IT, service delivery, and leadership.', 'Led budget monitoring, financial reporting, and project communication across PSG-FID and Fixed Income technology teams.']
  }]
}, {
  company: 'Hewlett-Packard',
  location: 'New York, NY',
  roles: [{
    title: 'Project Manager — Merrill Lynch Account',
    summary: 'Managed technology delivery and client engagement for the Merrill Lynch account, overseeing project execution, stakeholder coordination, and program reporting to ensure alignment with financial services operational and technology objectives.'
  }]
}, {
  company: 'Merrill Lynch',
  location: 'New York, NY & Hopewell, NJ',
  roles: [{
    title: 'Project Manager, Consultant',
    summary: 'Led strategic initiatives across risk, technology, and regulatory programs — delivering project governance, stakeholder coordination, and execution oversight within enterprise financial services environments.'
  }, {
    title: 'Senior Project Leader, Consultant',
    summary: 'Led strategic initiatives across risk, technology, and regulatory programs, delivering project governance, execution oversight, and stakeholder alignment within enterprise financial services environments.'
  }]
}, {
  company: 'Altria Group Inc.',
  location: 'Parsippany, NJ',
  roles: [{
    title: 'Exchange Administrator, Consultant',
    summary: 'Provided administration and operational support for Microsoft Exchange infrastructure, ensuring secure email operations, system performance, and user support across the enterprise environment.'
  }]
}, {
  company: 'Credit Suisse First Boston',
  location: 'New York, NY',
  roles: [{
    title: 'Exchange Administrator, Consultant',
    summary: 'Managed and supported enterprise Microsoft Exchange infrastructure, ensuring system stability, security, and efficient email services while providing technical support and administration across the organization.'
  }]
}, {
  company: 'Lucent Technologies — Bell Laboratories',
  location: 'Holmdel, NJ',
  roles: [{
    title: 'Project Manager, Advanced Technologies',
    summary: 'Led strategic technology research and innovation projects at Bell Labs, overseeing cross-functional teams, program execution, and stakeholder coordination to advance next-generation telecommunications solutions.'
  }]
}];
export function Experience() {
  return <section id="experience" className="py-24 md:py-32 bg-navy border-t border-white/5" data-id="element-80">
      <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-81">
        <FadeIn data-id="element-82">
          <div className="mb-16 md:mb-20" data-id="element-83">
            <div className="text-gold text-xs uppercase tracking-widest font-medium mb-6" data-id="element-84">
              Professional Tenure
            </div>
            <h3 className="text-3xl md:text-4xl lg:text-5xl font-serif text-softwhite leading-tight max-w-3xl" data-id="element-85">
              A legacy of leadership at top-tier institutions.
            </h3>
          </div>
        </FadeIn>

        <div className="space-y-16" data-id="element-86">
          {experience.map((company, ci) => <FadeIn key={ci} delay={0.05} data-id="element-87">
              <div className="border border-white/10 rounded-3xl bg-white/[0.02] p-8 md:p-12" data-id="element-88">
                <div className="flex flex-col md:flex-row md:items-baseline md:justify-between gap-2 mb-10 pb-6 border-b border-white/10" data-id="element-89">
                  <h4 className="text-2xl md:text-3xl font-serif text-softwhite" data-id="element-90">
                    {company.company}
                  </h4>
                  <div className="text-xs uppercase tracking-widest text-gold" data-id="element-91">
                    {company.location}
                  </div>
                </div>

                <div className="space-y-10" data-id="element-92">
                  {company.roles.map((role, ri) => <div key={ri} className="relative pl-6 border-l-2 border-gold/30 hover:border-gold transition-colors duration-500" data-id="element-93">
                      <div className="flex flex-col md:flex-row md:items-baseline md:justify-between mb-3 gap-2" data-id="element-94">
                        <h5 className="text-xl font-serif text-softwhite" data-id="element-95">
                          {role.title}
                        </h5>
                        {role.period && <span className="text-xs uppercase tracking-widest text-gold/80 font-medium" data-id="element-96">
                            {role.period}
                          </span>}
                      </div>
                      {role.summary && <p className="text-softwhite/65 text-sm md:text-base font-light leading-relaxed mb-4" data-id="element-97">
                          {role.summary}
                        </p>}
                      {role.bullets && <ul className="space-y-2" data-id="element-98">
                          {role.bullets.map((b, bi) => <li key={bi} className="flex items-start text-softwhite/60 text-sm font-light leading-relaxed" data-id="element-99">
                              <span className="text-gold mr-3 mt-2.5 flex-shrink-0 w-2 h-px bg-gold" data-id="element-100"></span>
                              <span data-id="element-101">{b}</span>
                            </li>)}
                        </ul>}
                    </div>)}
                </div>
              </div>
            </FadeIn>)}
        </div>
      </div>
    </section>;
}